import React, { useRef } from 'react';
import update from 'immutability-helper';
import theme from '@/constants/theme';
import Button from '@/components/input/Button';
import Textarea from '@/components/input/Textarea';
import { Circle, Delete, ViewCompact } from '@mui/icons-material';
import { useSortDrag, useSortDrop } from '@/hooks/useSortDnD';
import { ChoiceQuestion, isChoiceQuestion, Question, Quiz, RangeQuestion, typeDetails } from '../../common';
import QuestionOptions from './QuestionOptions';
import RangeOptions from './RangeOptions';

interface Props {
  type: Question['type'];
  id: Question['id'];
  question: Question['question'];
  options: Question['options'];
  config: Question['config'];
  setQuiz: React.Dispatch<React.SetStateAction<Quiz | null>>;
  index: number;
  moveQuestion: (fromIndex: number, toIndex: number) => void;
}

const EditableQuestion: React.FC<Props> = ({
  id,
  type,
  index,
  config,
  options,
  question,
  setQuiz,
  moveQuestion,
}) => {
  const QuestionIcon = typeDetails?.[type]?.Icon ?? Circle;
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);

  const handleChangeQuestion = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuiz((prevQuiz) =>
      update(prevQuiz, {
        questions: {
          [index]: {
            question: { $set: e.target.value },
          },
        },
      }),
    );
  };

  const handleRemoveQuestion = () => {
    setQuiz((prevQuiz) =>
      update(prevQuiz, {
        questions: {
          $splice: [[index, 1]],
        },
      }),
    );
  };

  const [, drop] = useSortDrop({
    ref: ref as React.RefObject<HTMLDivElement>,
    index,
    moveItem: moveQuestion,
    name: 'question',
  });

  const [{ isDragging }, drag] = useSortDrag({
    id,
    index,
    name: 'question',
  });

  drop(ref);
  drag(dragHandleRef);

  return (
    <div style={{ ...styles.itemContainer, opacity: isDragging ? 0.2 : 1 }} ref={ref}>
      <div style={styles.listIndicator}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>Orden {index + 1}</span>• <QuestionIcon />
          <span>{typeDetails?.[type]?.label ?? 'Pregunta'}</span>
        </div>
        <span
          ref={dragHandleRef}
          style={{
            cursor: 'grab',
            padding: 4,
            position: 'absolute',
            right: '50%',
            transform: 'translateX(50%)',
          }}
        >
          <ViewCompact />
        </span>
        <Button type="button" variant="ghost" size="small" Icon={Delete} onClick={handleRemoveQuestion}>
          Eliminar
        </Button>
      </div>
      <Textarea sx={{ minHeight: 50 }} value={question} onChange={handleChangeQuestion} />
      {type === 'text' && (
        <small style={{ color: theme.colors.text.caption }}>Este campo soporta texto tipo Markdown.</small>
      )}

      {type === 'range' && (
        <RangeOptions
          index={index}
          type={type}
          options={options as RangeQuestion['options']}
          setQuiz={setQuiz}
        />
      )}

      {isChoiceQuestion(type) && (
        <QuestionOptions
          type={type}
          options={options as ChoiceQuestion['options']}
          setQuiz={setQuiz}
          config={config}
          index={index}
          questionId={id}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  itemContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    color: theme.colors.text.body,
    backgroundColor: theme.colors.background.primary,
    borderRadius: 8,
    padding: 16,
    boxShadow:
      'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
  },
  listIndicator: {
    position: 'relative',
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.colors.text.caption,
  },
};

export default EditableQuestion;
